/**
 * Ask Redline Copilot — ChatGPT-style workspace for HOA managers and board members.
 *
 * Layout: left sidebar (past conversations) + main chat area (compact live-stat
 * tiles at top, message stream in middle, composer at bottom). Multi-turn
 * history is kept client-side in Phase 1 — reloading the page starts fresh.
 *
 * Backend contract:
 *   POST /api/tenants/{tenantId}/copilot/ask   → { ok, answer, evidence_preview, ... }
 *   GET  /api/tenants/{tenantId}/copilot/live-snapshot → { snapshot: {...} }
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight,
  Sparkles, Send, Plus, MessageSquare, Trash2, Loader2,
  AlertCircle, ChevronLeft, Menu, X, PanelLeftOpen,
  Mic, Square, Paperclip, FileText, AudioLines, Lightbulb, Cloud, Table2, RotateCw,
} from "lucide-react";
import { CopilotVoiceBar } from "./copilot/CopilotVoiceBar";
import { CopilotActionCard } from "./copilot/CopilotActionCard";
import { CopilotSignalCard } from "./copilot/CopilotSignalCard";
import { CopilotDraftCard } from "./copilot/CopilotDraftCard";
import { SuggestFeatureDialog } from "./copilot/SuggestFeatureDialog";
import { BudgetReviewDialog } from "./copilot/BudgetReviewDialog";
import { AnswerBody } from "./copilot/AnswerBody";
import { CopyButton } from "./copilot/CopyButton";
import { ThinkingIndicator } from "./copilot/ThinkingIndicator";
import { StarterChips } from "./copilot/StarterChips";
import { V } from "@/lib/vocab";

// iter379 — dictation hygiene: Whisper sometimes returns stray double spaces, spaces before
// punctuation and runs of blank lines; a second recording is appended as its own sentence.
const tidyTranscript = (t) => (t || "")
  .replace(/\r/g, "")
  .replace(/[ \t]+/g, " ")
  .replace(/ +([.,!?;:])/g, "$1")
  .replace(/\n{3,}/g, "\n\n")
  .split("\n").map((l) => l.trim()).join("\n")
  .trim();
const joinDictation = (cur, text) => {
  const a = (cur || "").trimEnd();
  if (!a) return text;
  const sep = /[.!?…:,;]$/.test(a) ? " " : ". ";
  return `${a}${sep}${text}`;
};

const HISTORY_CAP = 12;   // matches backend HISTORY_CAP
const CONV_STORAGE_KEY = "askredline.copilot.conversations";
const MAX_STORED_CONVS = 20;
const MAX_ATTACHMENTS = 5;          // matches backend ATTACH_MAX_PER_TURN
const MAX_ATTACH_MB = 20;           // matches backend ATTACH_MAX_BYTES
// The backend caps itself (12s brain + 48s synth), so anything past this is a dead connection,
// not a slow answer — fail it and offer Try again instead of spinning for ever.
const ASK_TIMEOUT_MS = 90_000;
const ATTACH_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.md,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tif,.tiff";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const newConvId = () => `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const loadStored = () => {
  try {
    const raw = localStorage.getItem(CONV_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed ? parsed : {};
  } catch {
    return {};
  }
};

const persistConvs = (convsByTenant) => {
  try {
    // Cap per-tenant conversation list length to keep localStorage tidy.
    const trimmed = {};
    for (const [tid, convs] of Object.entries(convsByTenant || {})) {
      trimmed[tid] = (convs || []).slice(0, MAX_STORED_CONVS);
    }
    localStorage.setItem(CONV_STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    /* ignore quota errors */
  }
};

const firstUserSnippet = (messages) => {
  const first = (messages || []).find((m) => m.role === "user");
  return (first?.content || "New conversation").slice(0, 60);
};

// ─────────────────────────────────────────────────────────────────────────────
// Message bubbles
// ─────────────────────────────────────────────────────────────────────────────

// Evidence pills used to render below every answer; removed on 2026-02-12
// per field feedback — the inline citations in the answer text (e.g. "per
// Section H-5") already carry the same information more naturally, and
// stacking 5 near-duplicate pills of the same document felt noisy on both
// desktop and mobile. The `evidence_preview` payload is still returned by
// the API for future use (source drawer, citation click-through, etc.).

const CopilotMessage = ({ msg, isLatest, tenantId, onPatch, me, threadTitle, communityName, onRevise, busy, onRetry }) => {
  const isUser = msg.role === "user";
  const files = msg.files || [];
  const thinking = !isUser && !msg.content && isLatest;
  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : ""}`} data-testid={`copilot-msg-${msg.role}`}>
      {!isUser && (
        <div className="h-8 w-8 rounded-full bg-ink text-white flex items-center justify-center shrink-0">
          <Sparkles className="h-4 w-4" strokeWidth={2.25} />
        </div>
      )}
      <div className={`group/msg max-w-[92%] sm:max-w-[80%] min-w-0 ${isUser ? "text-right" : ""}`}>
        {files.length > 0 && (
          <div className={`flex flex-wrap gap-1.5 mb-1.5 ${isUser ? "justify-end" : ""}`} data-testid="copilot-msg-files">
            {files.map((f) => (
              <span key={f.id}
                    className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-ink/70">
                <FileText className="h-3 w-3 text-redline-500" strokeWidth={2.25} />
                <span className="max-w-[180px] truncate">{f.filename}</span>
              </span>
            ))}
          </div>
        )}
        {(isUser || msg.content || thinking) && (
          <div
            className={`inline-block rounded-2xl px-4 py-2.5 text-sm leading-relaxed text-left ${
              isUser
                ? "bg-ink text-white rounded-tr-md whitespace-pre-wrap"
                : "bg-white border border-sand-200 text-ink rounded-tl-md"
            }`}
          >
            {isUser
              ? (msg.content || "")
              : msg.content
                ? <AnswerBody text={msg.content} />
                : <ThinkingIndicator />}
          </div>
        )}
        {!isUser && msg.content && (
          <div className="mt-0.5 -ml-1 flex items-center opacity-60 group-hover/msg:opacity-100 transition-opacity" data-testid="copilot-msg-tools">
            <CopyButton text={msg.content} testid="copilot-msg-copy" label="Copy" />
          </div>
        )}
        {msg.draft && (
          <CopilotDraftCard
            tenantId={tenantId}
            draft={msg.draft}
            communityName={communityName}
            onPatch={onPatch}
            onRevise={onRevise}
            busy={busy}
          />
        )}
        {msg.action && (
          <CopilotActionCard
            tenantId={tenantId}
            action={msg.action}
            result={msg.action_result}
            dismissed={msg.action_dismissed}
            onPatch={onPatch}
          />
        )}
        {msg.signal && (
          <CopilotSignalCard
            tenantId={tenantId}
            signal={msg.signal}
            result={msg.signal_result}
            dismissed={msg.signal_dismissed}
            onPatch={onPatch}
            me={me}
            threadTitle={threadTitle}
          />
        )}
        {msg.spoken && (
          <div className={`mt-1 text-[10px] uppercase tracking-wider text-ink/35 flex items-center gap-1 ${isUser ? "justify-end" : ""}`}
               data-testid="copilot-msg-spoken">
            <Mic className="h-2.5 w-2.5" strokeWidth={2.5} /> Spoken
          </div>
        )}
        {!isUser && msg.error && (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-rose-600">
            <span className="inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {msg.error}</span>
            {onRetry && (
              <button onClick={onRetry} disabled={busy} data-testid="copilot-retry"
                className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-white px-2.5 py-1 font-semibold text-rose-700 hover:border-rose-300 disabled:opacity-50">
                <RotateCw className="h-3 w-3" /> Try again
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Copilot page
// ─────────────────────────────────────────────────────────────────────────────

export const WAITING_PROMPT = "What's waiting on me right now?";

export default function Copilot() {
  const { tenantId, tenant, role, actualRole, meUser } = useOutletContext() || {};

  // Access gate: block residents on the client too (server enforces this too).
  const hasAccess = ["hoa_manager", "board_member", "company_admin", "regional_manager",
                     "super_admin", "platform_admin", "onboarding_admin", "operator"]
    .includes(actualRole || role);

  // Premium feature gate: the entire Copilot is a per-community entitlement toggled by
  // super_admin. Super/platform admins bypass the gate so they can demo & configure.
  const isSuperish = ["super_admin", "platform_admin"].includes(actualRole || role);
  const copilotEnabled = !!tenant?.copilot_enabled;
  const showUpgradePanel = tenant && !copilotEnabled && !isSuperish;

  // Voice is a premium per-community add-on with three positions (iter336):
  //   off          — no mic at all
  //   dictation    — mic → Whisper → text in the composer
  //   conversation — a live spoken exchange whose turns land in this same thread
  // Communities configured before iter336 carry only the old boolean, so fall back to it.
  const voiceMode = !copilotEnabled
    ? "off"
    : (tenant?.copilot_voice_mode || (tenant?.copilot_voice_enabled ? "dictation" : "off"));
  const voiceInputEnabled = voiceMode !== "off";
  const voiceChatEnabled = voiceMode === "conversation";

  const [allConvsByTenant, setAllConvsByTenant] = useState({});
  const [activeConvId, setActiveConvId] = useState(null);
  const [inputVal, setInputVal] = useState("");
  const [isPhone, setIsPhone] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const on = (e) => setIsPhone(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const [sending, setSending] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // The page fills the viewport below whatever sits above it (top bar + any banners), so it
  // never overlaps them and never scrolls the whole dashboard.
  const pageRef = useRef(null);
  const [pageTop, setPageTop] = useState(72);
  useLayoutEffect(() => {
    const measure = () => { if (pageRef.current) setPageTop(Math.max(0, Math.round(pageRef.current.getBoundingClientRect().top + window.scrollY))); };
    measure();
    const ro = typeof ResizeObserver !== "undefined" && pageRef.current?.parentElement ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(pageRef.current.parentElement);
    window.addEventListener("resize", measure);
    return () => { ro?.disconnect(); window.removeEventListener("resize", measure); };
  }, []);

  // Voice-input state — press mic → speak → whisper transcribes → text lands
  // in the composer for review/edit before send. Mirrors the HelpConcierge
  // pattern that's already deployed and proven.
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  // iter379 — the send arrow works WHILE recording: stop → transcribe → send in one tap.
  const sendAfterRef = useRef(false);
  const sendRef = useRef(null);
  // Voice conversation — a live spoken call whose turns are appended to the thread it started in.
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [budgetAtt, setBudgetAtt] = useState(null);
  const voiceConvRef = useRef(null);
  // Attachments (iter333) — a file rides along with ONE message: uploaded straight away
  // (so the read happens before they hit send), listed as chips under the composer, and
  // read for that answer only. Never added to the community's document library.
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(0);
  const fileRef = useRef(null);
  // Desktop sidebar collapse state — persists across page reloads so managers
  // who prefer a wider chat area (or a distraction-free view when reading long
  // legal answers) keep their setting. Not shared with mobile: the mobile
  // drawer already toggles independently on hamburger.
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(() => {
    try {
      return localStorage.getItem("askredline.copilot.sidebarCollapsed") === "1";
    } catch {
      return false;
    }
  });
  const setSidebarCollapsed = (v) => {
    setSidebarCollapsedState(v);
    try { localStorage.setItem("askredline.copilot.sidebarCollapsed", v ? "1" : "0"); }
    catch { /* ignore quota */ }
  };

  const scrollerRef = useRef(null);
  const textareaRef = useRef(null);
  // iter379 — the composer grows with its content (up to ~6 lines) instead of cramming a
  // multi-line question into one scrolling row.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [inputVal]);

  // Restore conversations from localStorage once (offline cache), then let the server win.
  useEffect(() => {
    setAllConvsByTenant(loadStored());
  }, []);

  // ── Server persistence (iter387) — threads live on the account, per community, 90 days.
  // Load: merge server threads over the local cache (newest updated_at wins); any thread that
  // only exists locally is pushed up once. Save: every changed thread is PUT ~1s after it
  // settles (never while an answer is still streaming). Delete: immediate.
  const syncedRef = useRef({});          // id → updated_at last confirmed on the server
  const loadedForRef = useRef(null);     // tenant id whose threads have been fetched
  const [threadsLoaded, setThreadsLoaded] = useState(false);
  useEffect(() => {
    if (!tenantId || !hasAccess) return;
    let cancelled = false;
    setThreadsLoaded(false);
    api.get(`/tenants/${tenantId}/copilot/threads`).then((r) => {
      if (cancelled) return;
      const server = r.data?.threads || [];
      const byId = Object.fromEntries(server.map((t) => [t.id, t]));
      server.forEach((t) => { syncedRef.current[t.id] = t.updated_at; });
      setAllConvsByTenant((prev) => {
        const local = prev[tenantId] || [];
        const merged = {};
        for (const t of server) merged[t.id] = t;
        for (const c of local) {
          const sv = byId[c.id];
          if (!sv || (c.updated_at || "") > (sv.updated_at || "")) merged[c.id] = c;
        }
        const list = Object.values(merged).sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
        const next = { ...prev, [tenantId]: list.slice(0, 40) };
        persistConvs(next);
        return next;
      });
      loadedForRef.current = tenantId;
      setThreadsLoaded(true);
    }).catch(() => { loadedForRef.current = tenantId; setThreadsLoaded(true); });
    return () => { cancelled = true; };
  }, [tenantId, hasAccess]);

  // Get the conversations list for THIS community.
  const conversations = useMemo(() => allConvsByTenant[tenantId] || [], [allConvsByTenant, tenantId]);
  const activeConv = useMemo(
    () => conversations.find((c) => c.id === activeConvId) || null,
    [conversations, activeConvId]
  );

  const syncTimerRef = useRef(null);
  const convsRef = useRef([]);
  useEffect(() => {
    convsRef.current = conversations;
    if (!tenantId || loadedForRef.current !== tenantId) return;
    clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      for (const c of convsRef.current) {
        if (!c?.id || (c.messages || []).some((m) => m.streaming)) continue;
        if (syncedRef.current[c.id] === c.updated_at) continue;
        const stamp = c.updated_at;
        api.put(`/tenants/${tenantId}/copilot/threads/${c.id}`, {
          title: c.title, messages: c.messages, created_at: c.created_at, updated_at: c.updated_at,
        }).then(() => { syncedRef.current[c.id] = stamp; }).catch(() => {});
      }
    }, 1000);
    return () => clearTimeout(syncTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, tenantId, threadsLoaded]);


  // Always OPEN on a fresh entry box (empty composer), never the last thread — the Copilot is an
  // "ask me anything" tool, so entering it should invite a new question. Prior chats stay in the
  // sidebar/history to reopen. Also resets to a clean entry when switching communities.
  useEffect(() => {
    if (!tenantId) return;
    if (activeConvId && conversations.some((c) => c.id === activeConvId)) return;
    setActiveConvId(null);
  }, [tenantId, conversations, activeConvId]);

  // Focus the entry box on first open so you can start typing immediately.
  useEffect(() => {
    const id = setTimeout(() => textareaRef.current?.focus(), 80);
    return () => clearTimeout(id);
  }, []);


  // ⌘K / deep link: /dashboard/copilot?ask=<question> starts a fresh thread with that question.
  const [searchParams, setSearchParams] = useSearchParams();
  const askedRef = useRef("");
  useEffect(() => {
    const q = (searchParams.get("ask") || "").trim();
    if (!q || !tenantId || !hasAccess || (!copilotEnabled && !isSuperish) || askedRef.current === q) return;
    askedRef.current = q;
    const next = new URLSearchParams(searchParams); next.delete("ask"); setSearchParams(next, { replace: true });
    setTimeout(() => sendMessage(q), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, tenantId, hasAccess, copilotEnabled, isSuperish]);

  // Auto-scroll to bottom when new messages arrive.
  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [activeConv?.messages?.length, sending]);

  // ── Conversation CRUD (local state + localStorage) ────────────────────────
  const setConvsForTenant = (mapper) => {
    setAllConvsByTenant((prev) => {
      const list = mapper(prev[tenantId] || []);
      const next = { ...prev, [tenantId]: list };
      persistConvs(next);
      return next;
    });
  };

  const createConv = () => {
    const conv = {
      id: newConvId(),
      title: "New conversation",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      messages: [],
    };
    setConvsForTenant((list) => [conv, ...list]);
    setActiveConvId(conv.id);
    setInputVal("");
    setTimeout(() => textareaRef.current?.focus(), 20);
  };

  const deleteConv = (cid) => {
    setConvsForTenant((list) => list.filter((c) => c.id !== cid));
    if (activeConvId === cid) setActiveConvId(null);
    delete syncedRef.current[cid];
    if (tenantId) api.delete(`/tenants/${tenantId}/copilot/threads/${cid}`).catch(() => {});
  };

  const updateActive = (mutator, forceConvId) => {
    const cid = forceConvId || activeConvId;
    if (!cid) return;
    setAllConvsByTenant((prev) => {
      const list = (prev[tenantId] || []).map((c) =>
        c.id === cid ? mutator({ ...c }) : c
      );
      const next = { ...prev, [tenantId]: list };
      persistConvs(next);
      return next;
    });
  };

  // Patch one message in place — how an action card records its outcome, so a refresh shows
  // "done" instead of a second live confirm button.
  const patchMessage = (idx, patch) => {
    updateActive((c) => {
      const msgs = [...c.messages];
      if (!msgs[idx]) return c;
      msgs[idx] = { ...msgs[idx], ...patch };
      return { ...c, messages: msgs, updated_at: new Date().toISOString() };
    });
  };

  // ── Voice conversation — one thread, spoken and typed mixed ───────────────
  const ensureConv = (title) => {
    if (activeConvId) return activeConvId;
    const cid = newConvId();
    const now = new Date().toISOString();
    setConvsForTenant((list) => [
      { id: cid, title, created_at: now, updated_at: now, messages: [] }, ...list]);
    setActiveConvId(cid);
    return cid;
  };

  const startVoice = () => {
    voiceConvRef.current = ensureConv("Spoken conversation");
    setVoiceOpen(true);
  };

  const addVoiceTurn = ({ role, content }) => {
    updateActive((c) => ({
      ...c,
      title: c.messages.length === 0 && role === "user" ? content.slice(0, 60) : c.title,
      messages: [...c.messages, { role, content, at: new Date().toISOString(), spoken: true }],
      updated_at: new Date().toISOString(),
    }), voiceConvRef.current);
  };

  const endVoice = () => { setVoiceOpen(false); };

  // A spoken request for an action puts the card in the thread — the call can't press it.
  const addVoiceProposal = (card) => {
    updateActive((c) => ({
      ...c,
      messages: [...c.messages, {
        role: "assistant", at: new Date().toISOString(), spoken: true, action: card,
        content: "Here's the card — press confirm and I'll do it.",
      }],
      updated_at: new Date().toISOString(),
    }), voiceConvRef.current);
  };

  // ── Voice input — record → whisper → text into composer ──────────────────
  const toggleRecording = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeCandidates = [
        "audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus",
      ];
      const mimeType = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m));
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1200) return;   // accidental tap → do nothing
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const r = await api.post("/help/transcribe", fd);
          const text = tidyTranscript(r.data?.text || "");
          if (text) {
            const merged = joinDictation(inputValRef.current, text);
            if (sendAfterRef.current) {
              sendAfterRef.current = false;
              setInputVal(merged);
              sendRef.current?.(merged);
            } else {
              setInputVal(merged);
              textareaRef.current?.focus();
            }
          } else if (sendAfterRef.current) {
            sendAfterRef.current = false;
            if (inputValRef.current.trim()) sendRef.current?.(inputValRef.current);
            else toast.message("Didn't catch anything — try again.");
          }
        } catch {
          sendAfterRef.current = false;
          toast.error("Couldn't transcribe that — try again.");
        } finally {
          setTranscribing(false);
        }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      setRecording(false);
      toast.error("Microphone access blocked — allow it in your browser settings.");
    }
  };

  // ── Attachments — upload on pick, one API call per file ───────────────────
  const pickFiles = async (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    if (!chosen.length) return;
    const room = MAX_ATTACHMENTS - attachments.length;
    if (room <= 0) {
      toast.error(`You can attach up to ${MAX_ATTACHMENTS} files to one message.`);
      return;
    }
    for (const f of chosen.slice(0, room)) {
      if (f.size > MAX_ATTACH_MB * 1024 * 1024) {
        toast.error(`${f.name} is over ${MAX_ATTACH_MB} MB — try a smaller file.`);
        continue;
      }
      setUploading((n) => n + 1);
      try {
        const fd = new FormData();
        fd.append("file", f, f.name);
        const r = await api.post(`/tenants/${tenantId}/copilot/attachments`, fd);
        const att = r.data?.attachment;
        if (att?.id) {
          setAttachments((list) => [...list, att]);
          if (!att.readable) {
            toast.warning(`I couldn't pull any text out of ${att.filename} — I'll still take a look, but ask me plainly if the answer looks thin.`);
          }
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Couldn't attach ${f.name}.`);
      } finally {
        setUploading((n) => Math.max(0, n - 1));
      }
    }
    textareaRef.current?.focus();
  };

  const removeAttachment = (id) => setAttachments((list) => list.filter((a) => a.id !== id));

  // ── Ask a question ────────────────────────────────────────────────────────
  // `retry` re-asks the LAST turn in place — the two bubbles are already on screen, so nothing is
  // appended and the thread doesn't end up with the same question twice. The owner hit a dropped
  // connection mid-answer and "didn't allow me to retry, just showed error".
  const sendMessage = async (override, retry) => {
    const typed = (typeof override === "string" ? override : inputVal).trim();
    const files = retry ? (retry.files || []) : attachments;
    if ((!typed && !files.length) || sending || uploading || !tenantId) return;
    const text = typed || "What can you tell me about the attached file(s)?";

    // Ensure we have a conversation to append to.
    let convId = activeConvId;
    const isNewConv = !convId;
    if (isNewConv) {
      convId = newConvId();
      setActiveConvId(convId);
    }

    const userMsg = {
      role: "user", content: text, at: new Date().toISOString(),
      files: files.map((f) => ({ id: f.id, filename: f.filename })),
    };
    const assistantMsg = {
      role: "assistant", content: "", at: new Date().toISOString(),
      streaming: true, evidence: [], error: null,
    };

    // Prior turns + files already in this thread, read STRAIGHT FROM STATE. These used to be filled
    // in from inside the setConvsForTenant updater, which React doesn't run until the next render —
    // long after the request body below was built. So every turn went out with history: [] and the
    // Copilot had no memory of the conversation or of a file attached three questions earlier
    // (the bug behind "I asked it to reference that same attachment and it asked me to attach again").
    const prior = (conversations.find((c) => c.id === convId)?.messages || [])
      // On a retry the failed pair is still in state, so step back over it or the model is handed
      // the same question twice.
      .slice(0, retry ? -2 : undefined);
    const priorForModel = prior
      .filter((m) => (m.role === "user") || (m.role === "assistant" && m.content))
      .slice(-HISTORY_CAP)
      .map((m) => ({ role: m.role, content: m.content }));
    const room = Math.max(0, MAX_ATTACHMENTS - files.length);
    const carriedIds = [...new Set(prior.flatMap((m) => (m.files || []).map((f) => f.id)))]
      .filter((id) => id && !files.some((f) => f.id === id))
      .slice(-room);

    setConvsForTenant((list) => {
      if (retry) {
        return list.map((c) => (c.id !== convId ? c : {
          ...c,
          messages: [...c.messages.slice(0, -1), assistantMsg],
          updated_at: new Date().toISOString(),
        }));
      }
      if (isNewConv) {
        const conv = {
          id: convId,
          title: text.slice(0, 60),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          messages: [userMsg, assistantMsg],
        };
        return [conv, ...list];
      }
      return list.map((c) => {
        if (c.id !== convId) return c;
        return {
          ...c,
          title: c.messages.length === 0 ? text.slice(0, 60) : c.title,
          updated_at: new Date().toISOString(),
          messages: [...c.messages, userMsg, assistantMsg],
        };
      });
    });

    setInputVal("");
    setAttachments([]);
    setSending(true);

    try {
      const payload = {
        message: text,
        history: priorForModel,
        attachment_ids: files.map((f) => f.id),
        carried_attachment_ids: carriedIds,
      };
      const ask = () => api.post(`/tenants/${tenantId}/copilot/ask`, payload, { timeout: ASK_TIMEOUT_MS });
      let res;
      try {
        res = await ask();
      } catch (e) {
        // A severed connection (a deploy swapping pods mid-request, a mobile blip) gives no HTTP
        // response at all. Resubmitting is safe — the endpoint caps itself and answers 200 even on
        // its own timeouts — so take one quiet second chance before bothering the manager.
        if (e?.response || e?.code === "ECONNABORTED") throw e;
        await new Promise((r) => setTimeout(r, 1200));
        res = await ask();
      }
      const data = res.data || {};
      const nextAssistant = {
        role: "assistant",
        at: new Date().toISOString(),
        streaming: false,
        content: data.ok ? (data.answer || "") : "",
        // iter338 — Copilot may propose an action; the card renders under the message and
        // nothing happens until the manager presses confirm.
        action: data.action || null,
        // Product listener — a capability Ask Redline lacks becomes a "send to the team?" card.
        signal: data.signal || null,
        // iter394 — a written document rides as a draft card: copy, PDF/Word on letterhead, revise.
        draft: data.draft || null,
        evidence: data.evidence_preview || [],
        error: data.ok
          ? null
          : (data.error === "brain_timeout"
              ? `The ${V.lc("org")} brain took longer than usual to load — try again in a moment.`
              : data.error === "synth_timeout"
              ? "That answer took too long and was cut off. Ask again — a more specific question or fewer attachments helps."
              : "Something went wrong pulling that answer. Try again."),
        model: data.model,
        elapsed_ms: data.elapsed_ms,
        live_sections: data.live_sections || [],
      };
      updateActive((c) => {
        const msgs = [...c.messages];
        msgs[msgs.length - 1] = nextAssistant;
        return { ...c, messages: msgs, updated_at: new Date().toISOString() };
      }, convId);
      // Snapshot may have shifted while they were reading — quietly refresh.
    } catch (err) {
      // No response at all (dropped mobile connection, a redeploy swapping the pod, a cut-off
      // request) comes back as axios' bare "Network Error" — which tells the manager nothing and
      // leaves them no way back. Say what happened and let them press Try again.
      const dropped = !err?.response;
      const msg = err?.response?.data?.detail
        || (dropped
          ? (err?.code === "ECONNABORTED"
            ? "That took too long to come back. Try again — a more specific question helps."
            : "Couldn't reach Ask Redline — the connection dropped. Your question is saved, press Try again.")
          : err?.message || "Request failed");
      updateActive((c) => {
        const msgs = [...c.messages];
        msgs[msgs.length - 1] = { ...msgs[msgs.length - 1], streaming: false, error: msg };
        return { ...c, messages: msgs };
      }, convId);
      toast.error(msg);
    } finally {
      setSending(false);
      setTimeout(() => textareaRef.current?.focus(), 20);
    }
  };

  // Re-ask the question above a failed answer, with whatever was attached to it.
  const retryLast = () => {
    const msgs = activeConv?.messages || [];
    const q = msgs[msgs.length - 2];
    if (!q || q.role !== "user" || sending) return;
    sendMessage(q.content, { files: q.files || [] });
  };

  const inputValRef = useRef("");
  inputValRef.current = inputVal;
  sendRef.current = sendMessage;

  // Send arrow: while recording it stops the mic and sends the transcript in one go.
  const onSendClick = () => {
    if (voiceInputEnabled && recording) {
      sendAfterRef.current = true;
      recRef.current?.stop();
      return;
    }
    sendMessage();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onSendClick();
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (!hasAccess) {
    return (
      <div className="rounded-2xl border border-sand-200 bg-white p-6 text-sm text-ink/60">
        Copilot is available to {V.lc("org")} managers and {V.lcs("board")}.
      </div>
    );
  }
  if (!tenantId) {
    return (
      <div className="rounded-2xl border border-sand-200 bg-white p-6 text-sm text-ink/60" data-testid="copilot-no-tenant">
        Pick {V.a("org")} above to start using the copilot.
      </div>
    );
  }

  if (showUpgradePanel) {
    return (
      <div className="max-w-2xl mx-auto pt-6" data-testid="copilot-upgrade-panel">
        <div className="rounded-3xl border border-sand-200 bg-white overflow-hidden">
          <div className="bg-gradient-to-br from-redline-50 via-white to-sand-50 px-6 pt-8 pb-6 text-center">
            <div className="h-14 w-14 mx-auto rounded-2xl bg-ink text-white flex items-center justify-center mb-4 shadow-lg">
              <Sparkles className="h-6 w-6" strokeWidth={2.25} />
            </div>
            <div className="inline-block text-[10px] font-bold uppercase tracking-[0.16em] text-redline-600 bg-redline-50 border border-redline-100 rounded-full px-3 py-1 mb-3">
              Premium
            </div>
            <h2 className="text-2xl font-heading font-bold text-ink mb-2">
              Ask Copilot for {tenant?.name || `this ${V.lc("org")}`}
            </h2>
            <p className="text-sm text-ink/60 max-w-md mx-auto">
              A workspace chat that reads your {V.lc("org")}'s CC&Rs, rules, contacts and
              live operational numbers, answers "how do I…" questions about using Redline, and
              reads any file you attach — so {V.lcs("board")} and managers get real answers in seconds.
            </p>
          </div>
          <div className="px-6 py-5 border-t border-sand-100 grid sm:grid-cols-2 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <ChevronRight className="h-4 w-4 text-redline-500 shrink-0 mt-0.5" strokeWidth={2.25} />
              <div>
                <div className="font-semibold text-ink">Grounded in your docs</div>
                <div className="text-xs text-ink/60">Cites specific sections from your governing documents.</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <ChevronRight className="h-4 w-4 text-redline-500 shrink-0 mt-0.5" strokeWidth={2.25} />
              <div>
                <div className="font-semibold text-ink">Live operational data</div>
                <div className="text-xs text-ink/60">Open requests, pending approvals, unanswered questions — surfaced automatically.</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <ChevronRight className="h-4 w-4 text-redline-500 shrink-0 mt-0.5" strokeWidth={2.25} />
              <div>
                <div className="font-semibold text-ink">{V.Grp()}-tier access</div>
                <div className="text-xs text-ink/60">Sees {V.grp()}-only material, respects every access rule.</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <ChevronRight className="h-4 w-4 text-redline-500 shrink-0 mt-0.5" strokeWidth={2.25} />
              <div>
                <div className="font-semibold text-ink">Voice (optional)</div>
                <div className="text-xs text-ink/60">Dictate a question hands-free, or hold a live spoken conversation.</div>
              </div>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-sand-100 bg-sand-50/60 text-center">
            <div className="text-xs text-ink/70">
              Ask Redline to enable Ask Copilot for {tenant?.name || `this ${V.lc("org")}`}.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activeMessages = activeConv?.messages || [];
  const isEmpty = activeMessages.length === 0;

  return (
    <div
      ref={pageRef}
      className="-mx-4 lg:-mx-8 -mb-4 lg:-mb-8 flex flex-col lg:flex-row"
      style={{ height: `calc(100vh - ${pageTop}px)` }}
      data-testid="copilot-page"
    >
      {/* Left: conversation list — persistent on desktop, slide-in drawer on mobile.
          Desktop can also be COLLAPSED (hidden) via the chevron in the sidebar header;
          a floating re-open tab lives on the left edge of the chat area when collapsed. */}
      {mobileNavOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/40"
          onClick={() => setMobileNavOpen(false)}
          data-testid="copilot-mobile-nav-backdrop"
        />
      )}
      <aside
        className={`flex flex-col w-72 lg:w-64 shrink-0 border-r border-sand-200 bg-sand-50/95 lg:bg-sand-50/40 ${
          mobileNavOpen
            ? "fixed lg:static inset-y-0 left-0 z-50 shadow-2xl lg:shadow-none"
            : sidebarCollapsed
              ? "hidden"
              : "hidden lg:flex"
        }`}
        data-testid="copilot-sidebar"
      >
        <div className="flex items-center gap-2 p-3 lg:pt-3 lg:pr-2 lg:pb-0 lg:pl-3">
          <button
            data-testid="copilot-new-conversation"
            onClick={() => { createConv(); setMobileNavOpen(false); }}
            className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-ink text-white text-sm font-semibold px-3 py-2.5 hover:bg-ink-700 transition-colors"
          >
            <Plus className="h-4 w-4" strokeWidth={2.5} /> New chat
          </button>
          {/* Desktop-only: collapse the sidebar */}
          <button
            className="hidden lg:flex h-10 w-10 rounded-xl border border-sand-200 items-center justify-center text-ink/60 hover:bg-white/70 transition-colors"
            onClick={() => setSidebarCollapsed(true)}
            aria-label="Hide conversations"
            title="Hide conversations"
            data-testid="copilot-sidebar-collapse"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          </button>
          {/* Mobile-only: close the drawer */}
          <button
            className="lg:hidden h-10 w-10 rounded-xl border border-sand-200 flex items-center justify-center text-ink/60"
            onClick={() => setMobileNavOpen(false)}
            aria-label="Close conversations"
            data-testid="copilot-mobile-nav-close"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-3 mt-1">
          {conversations.length === 0 && (
            <div className="text-center text-xs text-ink/40 px-3 py-6">
              No conversations yet. Ask anything about {tenant?.name || `your ${V.lc("org")}`}.
            </div>
          )}
          {conversations.map((c) => (
            <div
              key={c.id}
              className={`group flex items-center gap-2 rounded-xl px-2.5 py-2 mb-0.5 text-sm cursor-pointer ${
                c.id === activeConvId ? "bg-white border border-sand-200" : "hover:bg-white/70"
              }`}
              onClick={() => { setActiveConvId(c.id); setMobileNavOpen(false); }}
              data-testid={`copilot-conv-${c.id}`}
            >
              <MessageSquare className="h-3.5 w-3.5 text-ink/40 shrink-0" strokeWidth={2} />
              <div className="flex-1 min-w-0 truncate text-ink/80">
                {c.title || firstUserSnippet(c.messages)}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); deleteConv(c.id); }}
                className="opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity text-ink/30 hover:text-rose-500 p-1"
                data-testid={`copilot-conv-delete-${c.id}`}
                aria-label="Delete conversation"
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>
        <div className="px-3 py-2 border-t border-sand-200/70 text-[10.5px] text-ink/45 flex items-center gap-1.5" data-testid="copilot-threads-note">
          <Cloud className="h-3 w-3 shrink-0" strokeWidth={2} />
          {threadsLoaded ? "Saved to your account · kept 90 days" : "Loading your conversations…"}
        </div>
      </aside>

      {/* Right: chat area */}
      <section className="flex-1 flex flex-col min-w-0 bg-sand-50/20 relative">
        {/* Desktop reopen-tab + New chat: the only controls that survive collapsing the thread list.
            Starting a fresh chat used to mean re-opening the panel first (iter510). */}
        {sidebarCollapsed && (
          <div className="hidden lg:flex absolute left-2 top-4 z-10 items-center gap-1.5">
            <button
              className="h-9 w-9 rounded-xl border border-sand-200 bg-white flex items-center justify-center text-ink/60 hover:bg-sand-50 hover:border-sand-300 transition-colors shadow-sm"
              onClick={() => setSidebarCollapsed(false)}
              aria-label="Show conversations"
              title="Show conversations"
              data-testid="copilot-sidebar-expand"
            >
              <PanelLeftOpen className="h-4 w-4" strokeWidth={2} />
            </button>
            <button
              className="h-9 rounded-xl bg-ink text-white flex items-center gap-1.5 px-3 text-xs font-semibold hover:bg-ink-700 transition-colors shadow-sm"
              onClick={() => createConv()}
              aria-label="New chat"
              title="Start a new chat"
              data-testid="copilot-new-conversation-collapsed"
            >
              <Plus className="h-3.5 w-3.5" strokeWidth={2.5} /> New chat
            </button>
          </div>
        )}
        {/* Header + live tiles */}
        <div className={`border-b border-sand-200 bg-white px-4 lg:px-6 py-3.5 ${sidebarCollapsed ? "lg:pl-44" : ""}`}>
          <div className="flex items-center gap-2">
            <button
              className="lg:hidden h-9 w-9 rounded-xl border border-sand-200 flex items-center justify-center text-ink/70 shrink-0"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open conversations"
              data-testid="copilot-mobile-nav-open"
            >
              <Menu className="h-4 w-4" strokeWidth={2} />
            </button>
            <div className="h-8 w-8 rounded-xl bg-ink text-white flex items-center justify-center shrink-0">
              <Sparkles className="h-4 w-4" strokeWidth={2.25} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-heading font-bold text-ink truncate" data-testid="copilot-title">
                {tenant?.name ? `${tenant.name} Copilot` : "Copilot"}
              </div>
              <div className="text-[11px] text-ink/50 truncate">
                Your {V.lc("org")}&apos;s brain, live operations, and how to use Redline
              </div>
            </div>
            {tenant?.copilot_listener_enabled !== false && (
            <button
              onClick={() => setSuggestOpen(true)}
              title="Suggest a feature — tell the Ask Redline team what you wish Copilot could do"
              className="h-9 shrink-0 rounded-full border border-amber-200 bg-amber-50 text-amber-800 flex items-center gap-1.5 px-3 text-xs font-semibold hover:bg-amber-100 transition-colors"
              data-testid="copilot-suggest-feature"
              aria-label="Suggest a feature"
            >
              <Lightbulb className="h-3.5 w-3.5" strokeWidth={2.25} />
              <span className="hidden sm:inline">Suggest a feature</span>
            </button>
            )}
          </div>
          <SuggestFeatureDialog open={suggestOpen} onOpenChange={setSuggestOpen} tenantId={tenantId} me={meUser} />
          <BudgetReviewDialog open={!!budgetAtt} onOpenChange={(o) => { if (!o) setBudgetAtt(null); }} tenantId={tenantId} attachment={budgetAtt} />
        </div>

        {/* Messages */}
        <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 lg:px-6 py-6 space-y-4" data-testid="copilot-messages">
          {isEmpty ? (
            <div className="max-w-2xl mx-auto pt-4" data-testid="copilot-empty-state">
              <div className="h-14 w-14 mx-auto rounded-2xl bg-ink text-white flex items-center justify-center mb-4">
                <Sparkles className="h-6 w-6" strokeWidth={2.25} />
              </div>
              <h2 className="text-center text-2xl font-heading font-bold text-ink mb-2">
                What can I help with?
              </h2>
              <p className="text-center text-sm text-ink/60 mb-7">
                Ask about {tenant?.name || `this ${V.lc("org")}`}, draft on your letterhead, or check a budget.
              </p>
              <StarterChips tenant={tenant} onPick={(t) => { setInputVal(t.prompt); textareaRef.current?.focus(); if (t.needsFile && !attachments.length) toast.message("Attach the file first, then send."); }} />
            </div>
          ) : (
            activeMessages.map((m, i) => (
              <CopilotMessage
                key={i}
                msg={m}
                tenantId={tenantId}
                me={meUser}
                threadTitle={activeConv?.title || ""}
                communityName={tenant?.name}
                onPatch={(patch) => patchMessage(i, patch)}
                onRevise={(p) => sendMessage(p)}
                onRetry={m.role === "assistant" && m.error && i === activeMessages.length - 1
                  ? () => retryLast() : undefined}
                busy={sending}
                isLatest={i === activeMessages.length - 1 && sending && m.role === "assistant"}
              />
            ))
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-sand-200 bg-white px-4 lg:px-6 py-3">
          {voiceOpen && (
            <CopilotVoiceBar
              tenantId={tenantId}
              tenantName={tenant?.name}
              history={(activeConv?.messages || [])
                .filter((m) => m.content)
                .slice(-6)
                .map((m) => ({ role: m.role, content: m.content }))}
              onTurn={addVoiceTurn}
              onProposal={addVoiceProposal}
              onClose={endVoice}
            />
          )}
          {(attachments.length > 0 || uploading > 0) && (
            <div className="max-w-3xl mx-auto flex flex-wrap items-center gap-1.5 mb-2" data-testid="copilot-attachment-chips">
              {attachments.map((a) => (
                <span key={a.id} data-testid={`copilot-attachment-${a.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-sand-50 pl-2.5 pr-1.5 py-1 text-xs text-ink/80">
                  <FileText className="h-3.5 w-3.5 text-redline-500 shrink-0" strokeWidth={2.25} />
                  <span className="max-w-[200px] truncate font-semibold">{a.filename}</span>
                  {["xlsx", "csv"].includes(a.ext) && (
                    <button onClick={() => setBudgetAtt(a)} title="Review & correct this budget"
                            data-testid={`copilot-budget-review-${a.id}`}
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors px-2 py-0.5 text-[11px] font-bold">
                      <Table2 className="h-3 w-3" strokeWidth={2.5} /> Review
                    </button>
                  )}
                  <button onClick={() => removeAttachment(a.id)} aria-label={`Remove ${a.filename}`}
                          data-testid={`copilot-attachment-remove-${a.id}`}
                          className="text-ink/30 hover:text-rose-500 transition-colors p-0.5">
                    <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </button>
                </span>
              ))}
              {uploading > 0 && (
                <span className="inline-flex items-center gap-1.5 text-xs text-ink/50" data-testid="copilot-attachment-uploading">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading {uploading === 1 ? "your file" : `${uploading} files`}…
                </span>
              )}
            </div>
          )}
          <div className="flex items-end gap-1.5 sm:gap-2 max-w-3xl mx-auto">
            <input ref={fileRef} type="file" multiple accept={ATTACH_ACCEPT} className="hidden"
                   onChange={pickFiles} data-testid="copilot-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={sending || attachments.length >= MAX_ATTACHMENTS}
              title="Attach a file — PDF, Word, Excel, PowerPoint, CSV or an image"
              className="h-11 w-11 sm:h-12 sm:w-12 rounded-2xl border border-sand-200 bg-white text-ink/60 flex items-center justify-center hover:bg-sand-50 hover:text-ink transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="copilot-attach"
              aria-label="Attach a file"
            >
              <Paperclip className="h-4 w-4" strokeWidth={2} />
            </button>
            <textarea
              ref={textareaRef}
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={
                voiceInputEnabled && recording
                  ? "Listening…"
                  : voiceInputEnabled && transcribing
                    ? "Transcribing…"
                    : isPhone
                      ? "Ask anything…"
                      : `Ask about ${tenant?.name || `this ${V.lc("org")}`} — or how to use Redline…`
              }
              rows={1}
              className="flex-1 min-w-0 resize-none rounded-2xl border border-sand-200 focus:border-redline-300 focus:ring-2 focus:ring-redline-500/20 bg-white px-3.5 sm:px-4 py-3 text-sm leading-relaxed outline-none max-h-40 overflow-y-auto whitespace-pre-wrap"
              style={{ minHeight: isPhone ? "44px" : "48px" }}
              disabled={sending || (voiceInputEnabled && recording)}
              data-testid="copilot-input"
            />
            {voiceChatEnabled && !voiceOpen && (
              <button
                onClick={startVoice}
                disabled={sending || recording || transcribing}
                title="Talk to Copilot — a live spoken conversation"
                className="h-11 w-11 sm:h-12 sm:w-12 rounded-2xl border border-sand-200 bg-white text-ink/60 flex items-center justify-center hover:bg-sand-50 hover:text-ink transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="copilot-voice-start"
                aria-label="Talk to Copilot"
              >
                <AudioLines className="h-4 w-4" strokeWidth={2.25} />
              </button>
            )}
            {voiceInputEnabled && (
              <button
                onClick={toggleRecording}
                disabled={sending || transcribing}
                title={recording ? "Stop recording" : "Speak your question"}
                className={`h-11 w-11 sm:h-12 sm:w-12 rounded-2xl flex items-center justify-center shrink-0 transition-colors border ${
                  recording
                    ? "bg-redline-500 text-white border-redline-500 animate-pulse"
                    : "bg-white text-ink/60 border-sand-200 hover:bg-sand-50 hover:text-ink"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
                data-testid="copilot-mic"
                aria-label={recording ? "Stop recording" : "Speak your question"}
              >
                {transcribing
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : recording
                    ? <Square className="h-3.5 w-3.5" strokeWidth={2.5} />
                    : <Mic className="h-4 w-4" strokeWidth={2} />}
              </button>
            )}
            <button
              onClick={onSendClick}
              disabled={sending || uploading > 0 || transcribing || (!(voiceInputEnabled && recording) && !inputVal.trim() && !attachments.length)}
              title={voiceInputEnabled && recording ? "Stop recording and send" : "Send"}
              className={`h-11 w-11 sm:h-12 sm:w-12 rounded-2xl text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0 ${
                voiceInputEnabled && recording ? "bg-redline-500 hover:bg-redline-600 ring-2 ring-redline-500/30" : "bg-ink hover:bg-ink-700"}`}
              data-testid="copilot-send"
              aria-label={voiceInputEnabled && recording ? "Stop recording and send" : "Send"}
            >
              {sending || transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={2.25} />}
            </button>
          </div>
          <div className="text-[11px] text-ink/45 text-center mt-1.5" data-testid="copilot-disclaimer">
            Copilot can make mistakes. Check important info before you act on it or send it.
            <span className="hidden sm:inline"> Attachments are read for this conversation only.</span>
          </div>
        </div>
      </section>
    </div>
  );
}
